// ============================================================================
// GL REPLACEMENT TRANSACTION HOOKS
// ============================================================================

import { useState, useEffect, useCallback } from 'react';
import { glReplacementService } from 'api/services/glReplacementService';
import type { GlReplacementTransaction, CreateGlReplacementPayload, UpdateGlReplacementPayload } from 'api/types/glReplacement.types';
import { notifyDataChanged, useDataChangeListener } from './useDataSync';

const DATA_KEY = 'glReplacements';

export const useGlReplacements = () => {
  const [data, setData] = useState<GlReplacementTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchGlReplacements = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await glReplacementService.getAll({ pageSize: 50000 });
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGlReplacements();
  }, [fetchGlReplacements]);

  useDataChangeListener(DATA_KEY, fetchGlReplacements);

  return { data, loading, error, refetch: fetchGlReplacements };
};

export const useCreateGlReplacement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const createGlReplacement = useCallback(async (payload: CreateGlReplacementPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await glReplacementService.create(payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { createGlReplacement, loading, error };
};

export const useUpdateGlReplacement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const updateGlReplacement = useCallback(async (id: string, payload: UpdateGlReplacementPayload) => {
    setLoading(true);
    setError(null);
    try {
      const response = await glReplacementService.update(id, payload);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { updateGlReplacement, loading, error };
};

export const useSubmitGlReplacement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const submitGlReplacement = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await glReplacementService.submit(id);
      notifyDataChanged(DATA_KEY);
      return response.data;
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { submitGlReplacement, loading, error };
};

export const useDeleteGlReplacement = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteGlReplacement = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      await glReplacementService.delete(id);
      notifyDataChanged(DATA_KEY);
    } catch (err) {
      setError(err as Error);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { deleteGlReplacement, loading, error };
};
